import React, { useEffect, useRef, useState } from 'react';
import { errorToast, successToast, useOutsideClick } from '../../utils/helper';
import { Calendar } from 'react-calendar';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import Loader from '../../component/common/Loader';
import { Api } from '../../api';

const AddCompanyModal = ({ isOpen, onClose, onSuccess, editData = null, isEditMode = false }) => {
  const [loader, setLoader] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [expiryDate, setExpiryDate] = useState(
    new Date(new Date().setFullYear(new Date().getFullYear() + 1))
  );
  const [billingAmount, setBillingAmount] = useState('');
  const [noOfSeats, setNoOfSeats] = useState('');
  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [isExpiryDateOpen, setIsExpiryDateOpen] = useState(false);
  const [schemeId, setSchemeId] = useState(null);

  // Searchable dropdown states
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [companySearchText, setCompanySearchText] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [errors, setErrors] = useState({});

  const startDateRef = useRef(null);
  const expiryDateRef = useRef(null);
  const dropdownRef = useRef(null);
  const dropdownListRef = useRef(null);

  useOutsideClick(startDateRef, () => {
    setIsStartDateOpen(false);
  });

  useOutsideClick(expiryDateRef, () => {
    setIsExpiryDateOpen(false);
  });

  useOutsideClick(dropdownRef, () => {
    setCompanyDropdownOpen(false);
    // Reset search text to display selected company if any
    if (selectedCompany) {
      setCompanySearchText(displayText);
    }
  });

  useEffect(() => {
    console.log(editData);
    if (isOpen) {
      if (editData && editData.companyId) {
        fetchSchemeByCompanyId(editData.companyId);
      } else {
        fetchCompanies(1, '', false);
      }
    }
  }, [isOpen, editData]);

  // Handle search input change with debounce
  useEffect(() => {
    if (!companyDropdownOpen) return;
    // Don't trigger API call if we already have companies and search is empty
    if (companies.length > 0 && companySearchText === '') return;
    const delayDebounceFn = setTimeout(() => {
      setCurrentPage(1);
      fetchCompanies(1, companySearchText, false);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [companySearchText, companyDropdownOpen]);

  const fetchCompanies = async (page = 1, searchKey = '', isAppending = false) => {
    const isInitialLoad = page === 1 && searchKey === '' && !isAppending;

    if (isAppending) {
      setIsLoadingMore(true);
    }

    if (!isInitialLoad && !isAppending) {
      setIsSearching(true);
    }

    if (isInitialLoad) {
      setLoader(true);
    }

    try {
      const response = await Api.getSalarySacrificeCompaniesList(page, 25, searchKey);

      if (response?.data?.meta?.code === 1) {
        const companyList = (response?.data?.data || []).map((company) => ({
          _id: company._id,
          company_name: company.company_name,
          adminId: company._id
        }));

        if (isAppending) {
          setCompanies((prev) => [...prev, ...companyList]);
        } else {
          setCompanies(companyList);
        }

        setCurrentPage(page);
        setHasMore(companyList.length === 25);
      } else {
        errorToast(response?.data?.message || 'Failed to fetch companies');
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      errorToast('Failed to fetch companies');
      setCompanies([]);
    } finally {
      if (isInitialLoad) setLoader(false);
      if (isAppending) setIsLoadingMore(false);
      if (!isInitialLoad && !isAppending) setIsSearching(false);
    }
  };

  const fetchSchemeByCompanyId = async (companyId) => {
    setLoader(true);
    try {
      const response = await Api.getActiveHealthSchemeByCompId(companyId);

      if (response?.data?.meta?.code === 1) {
        const schemeData = response?.data?.data;

        if (schemeData) {
          setSchemeId(schemeData.schemeId);
          setSelectedCompany({
            _id: companyId,
            company_name: schemeData.companyName,
            adminId: companyId
          });
          setDisplayText(schemeData.companyName);
          setCompanySearchText(schemeData.companyName);
          setStartDate(new Date(schemeData.startDate));
          setExpiryDate(new Date(schemeData.expiryDate));
          setBillingAmount(schemeData.billingAmount?.toString() || '');
          setNoOfSeats(schemeData.seats?.toString() || '');
        }
      } else {
        errorToast(response?.data?.message || 'Failed to fetch scheme details');
      }
    } catch (error) {
      console.error('Error fetching scheme:', error);
      errorToast('Failed to fetch scheme details');
    } finally {
      setLoader(false);
    }
  };

  // Handle company selection
  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setDisplayText(company.company_name);
    setCompanySearchText(company.company_name);
    setCompanyDropdownOpen(false);
  };

  const handleSearchFocus = () => {
    // When focusing on the search field, clear it to allow new search
    if (selectedCompany) {
      setCompanySearchText('');
      // Reset companies to force fresh fetch when search is cleared
      setCompanies([]);
      setCurrentPage(1);
      fetchCompanies(1, '', false);
    } else if (companies.length === 0) {
      // Only load companies if we don't already have them
      fetchCompanies(1, '', false);
    }
    setCompanyDropdownOpen(true);
  };

  // Handle scroll for infinite loading
  const handleDropdownScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // If scrolled to bottom (with a small buffer)
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (!isLoadingMore && hasMore) {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        fetchCompanies(nextPage, companySearchText, true);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!selectedCompany) {
      newErrors.company = 'Please select a company';
    }

    if (!billingAmount || parseFloat(billingAmount) <= 0) {
      newErrors.billingAmount = 'Please enter a valid billing amount';
    }

    if (!noOfSeats || parseInt(noOfSeats) <= 0) {
      newErrors.noOfSeats = 'Please enter a valid number of seats';
    }

    if (new Date(expiryDate) <= new Date(startDate)) {
      newErrors.expiryDate = 'Expiry date must be after start date';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    setLoader(true);
    try {
      const reqBody = {
        companyId: selectedCompany._id,
        startDate: startDate,
        expiryDate: expiryDate,
        billingAmount: parseFloat(billingAmount),
        seats: parseInt(noOfSeats),
        isCustomPlan: false
      };

      let response;
      if (schemeId && isEditMode) {
        // Update existing scheme
        delete reqBody.companyId;
        response = await Api.UpdateCustomHealthScheme(schemeId, reqBody);
      } else {
        // Create new scheme
        response = await Api.createShoorahHealthScheme(reqBody);
      }

      if (response?.data?.meta?.code === 1) {
        onSuccess();
        handleClose();
      } else {
        errorToast(
          response?.data?.meta?.message ||
            `Failed to ${schemeId && isEditMode ? 'update scheme' : 'add company'}`
        );
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoader(false);
    }
  };

  const handleClose = () => {
    setSelectedCompany(null);
    setCompanySearchText('');
    setDisplayText('');
    setStartDate(new Date());
    setExpiryDate(new Date(new Date().setFullYear(new Date().getFullYear() + 1)));
    setBillingAmount('');
    setNoOfSeats('');
    setIsStartDateOpen(false);
    setIsExpiryDateOpen(false);
    setCompanyDropdownOpen(false);
    setSchemeId(null);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {loader && <Loader />}
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose}></div>

      {/* Modal */}
      <div className="relative bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4 p-6 pb-0">
          <h2 className="text-xl font-semibold text-shoorah-primary dark:text-white">
            {schemeId && !isEditMode ? 'View' : schemeId && isEditMode ? 'Edit' : 'Add'} Company
            Health Scheme
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6">
          <div className="space-y-4">
            {/* Company Selection - Searchable Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company <span className="text-red-500">*</span>
              </label>
              <div className="w-full" ref={dropdownRef}>
                <div className="relative">
                  <div
                    className={`w-full bg-white dark:bg-shoorah-darkBgColor rounded-lg pl-[15px] border-2 border-gray-300 dark:border-gray-600 py-2 focus:outline-none flex justify-between items-center ${
                      schemeId ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                    onClick={() => !schemeId && setCompanyDropdownOpen(!companyDropdownOpen)}
                  >
                    <input
                      className={`w-full bg-transparent border-none focus:outline-none text-black dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                        schemeId ? 'cursor-not-allowed' : 'cursor-text'
                      }`}
                      placeholder="Search for a company..."
                      name="companySearch"
                      value={companySearchText}
                      onChange={(e) => !schemeId && setCompanySearchText(e.target.value)}
                      onFocus={() => !schemeId && handleSearchFocus()}
                      onClick={(e) => {
                        if (!schemeId) {
                          e.stopPropagation();
                          setCompanyDropdownOpen(true);
                        }
                      }}
                      disabled={!!schemeId}
                    />
                    <div className="pr-3">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-black dark:text-white"
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  {companyDropdownOpen && (
                    <div
                      className="absolute z-10 w-full mt-1 bg-white dark:bg-shoorah-darkBgTabColor border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                      ref={dropdownListRef}
                      onScroll={handleDropdownScroll}
                    >
                      {isSearching ? (
                        <div className="px-4 py-2 text-center text-gray-500 dark:text-gray-400">
                          <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                          <span className="ml-2">Searching...</span>
                        </div>
                      ) : companies.length > 0 ? (
                        <>
                          {companies.map((company, index) => (
                            <div
                              key={index}
                              className={`px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                                selectedCompany && selectedCompany._id === company._id
                                  ? 'bg-gray-100 dark:bg-gray-700 font-semibold'
                                  : ''
                              }`}
                              onClick={() => handleCompanySelect(company)}
                            >
                              {company.company_name
                                ? company.company_name
                                    .split(' ')
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                                    )
                                    .join(' ')
                                : 'N/A'}
                            </div>
                          ))}
                          {isLoadingMore && (
                            <div className="px-4 py-2 text-center text-gray-500 dark:text-gray-400">
                              <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                              <span className="ml-2">Loading more...</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="px-4 py-2 text-gray-500 dark:text-gray-400">
                          {loader ? 'Loading...' : 'No companies found'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {errors.company && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.company}</p>
              )}
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <div className="relative w-full">
                <button
                  type="button"
                  onClick={() =>
                    schemeId && !isEditMode ? null : setIsStartDateOpen(!isStartDateOpen)
                  }
                  className={`flex flex-row justify-between w-full appearance-none bg-white dark:bg-shoorah-darkBgColor text-black dark:text-white rounded-lg px-3 border border-gray-300 dark:border-gray-600 py-2 focus:outline-none ${
                    schemeId && !isEditMode ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                  disabled={schemeId && !isEditMode}
                >
                  <span>{startDate.toLocaleDateString()}</span>
                  <svg
                    className="mt-1 ml-1 text-black dark:text-white"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {isStartDateOpen && (
                  <div
                    ref={startDateRef}
                    className="absolute z-20 mt-1 bg-white dark:bg-shoorah-darkBgColor shadow-lg rounded-lg border border-gray-200 dark:border-shoorah-darkBorder"
                  >
                    <Calendar
                      className="calendar"
                      onChange={(date) => {
                        setStartDate(date);
                        setIsStartDateOpen(false);
                        // Auto-adjust expiry date if needed
                        if (expiryDate <= date) {
                          const newExpiry = new Date(date);
                          newExpiry.setFullYear(newExpiry.getFullYear() + 1);
                          setExpiryDate(newExpiry);
                        }
                        // Clear date errors when changing dates
                        if (errors.expiryDate) {
                          setErrors((prev) => ({ ...prev, expiryDate: undefined }));
                        }
                      }}
                      minDate={new Date()}
                      value={startDate}
                      nextLabel={
                        <button
                          type="button"
                          className="flex justify-center items-center text-black dark:text-white"
                        >
                          <ChevronRightIcon className="w-8 h-8" />
                        </button>
                      }
                      prevLabel={
                        <button
                          type="button"
                          className="flex justify-center items-center text-black dark:text-white"
                        >
                          <ChevronLeftIcon className="w-8 h-8" />
                        </button>
                      }
                      navigationLabel={({ date }) => (
                        <button
                          type="button"
                          style={{ cursor: 'default', pointerEvents: 'none' }}
                          className="text-base text-black dark:text-white"
                        >
                          {date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long'
                          })}
                        </button>
                      )}
                    />
                  </div>
                )}
              </div>
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.startDate}</p>
              )}
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Expiry Date <span className="text-red-500">*</span>
              </label>
              <div className="relative w-full">
                <button
                  type="button"
                  onClick={() =>
                    schemeId && !isEditMode ? null : setIsExpiryDateOpen(!isExpiryDateOpen)
                  }
                  className={`flex flex-row justify-between w-full appearance-none bg-white dark:bg-shoorah-darkBgColor text-black dark:text-white rounded-lg px-3 border border-gray-300 dark:border-gray-600 py-2 focus:outline-none ${
                    schemeId && !isEditMode ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                  disabled={schemeId && !isEditMode}
                >
                  <span>{expiryDate.toLocaleDateString()}</span>
                  <svg
                    className="mt-1 ml-1 text-black dark:text-white"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {isExpiryDateOpen && (
                  <div
                    ref={expiryDateRef}
                    className="absolute z-20 mt-1 bg-white dark:bg-shoorah-darkBgColor shadow-lg rounded-lg border border-gray-200 dark:border-shoorah-darkBorder"
                  >
                    <Calendar
                      className="calendar"
                      onChange={(date) => {
                        setExpiryDate(date);
                        setIsExpiryDateOpen(false);
                        // Clear date errors when changing dates
                        if (errors.expiryDate) {
                          setErrors((prev) => ({ ...prev, expiryDate: undefined }));
                        }
                      }}
                      minDate={startDate}
                      value={expiryDate}
                      nextLabel={
                        <button
                          type="button"
                          className="flex justify-center items-center text-black dark:text-white"
                        >
                          <ChevronRightIcon className="w-8 h-8" />
                        </button>
                      }
                      prevLabel={
                        <button
                          type="button"
                          className="flex justify-center items-center text-black dark:text-white"
                        >
                          <ChevronLeftIcon className="w-8 h-8" />
                        </button>
                      }
                      navigationLabel={({ date }) => (
                        <button
                          type="button"
                          style={{ cursor: 'default', pointerEvents: 'none' }}
                          className="text-base text-black dark:text-white"
                        >
                          {date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long'
                          })}
                        </button>
                      )}
                    />
                  </div>
                )}
              </div>
              {errors.expiryDate && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.expiryDate}</p>
              )}
            </div>

            {/* Billing Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Billing Amount (£) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={billingAmount}
                onChange={(e) => {
                  setBillingAmount(e.target.value);
                  if (errors.billingAmount) {
                    setErrors((prev) => ({ ...prev, billingAmount: undefined }));
                  }
                }}
                placeholder="Enter billing amount"
                className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-shoorah-primary focus:border-shoorah-primary dark:bg-shoorah-darkBgColor dark:text-white ${
                  schemeId && !isEditMode ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                disabled={schemeId && !isEditMode}
              />
              {errors.billingAmount && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.billingAmount}
                </p>
              )}
            </div>

            {/* No of Seats */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                No of Seats <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={noOfSeats}
                onChange={(e) => {
                  setNoOfSeats(e.target.value);
                  if (errors.noOfSeats) {
                    setErrors((prev) => ({ ...prev, noOfSeats: undefined }));
                  }
                }}
                placeholder="Enter number of seats"
                className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-shoorah-primary focus:border-shoorah-primary dark:bg-shoorah-darkBgColor dark:text-white ${
                  schemeId && !isEditMode ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                disabled={schemeId && !isEditMode}
              />
              {errors.noOfSeats && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.noOfSeats}</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pb-6 px-6">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              {schemeId && !isEditMode ? 'Close' : 'Cancel'}
            </button>
            {!(schemeId && !isEditMode) && (
              <button
                type="submit"
                className="btn-primary flex-1"
              >
                {schemeId && isEditMode ? 'Update Scheme' : 'Add Company'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCompanyModal;
